import { useEffect, useMemo, useState } from "react";
import { Col, Form, InputNumber, Modal, Row } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { FaDollarSign } from "react-icons/fa";
import { FaFileInvoiceDollar } from "react-icons/fa6";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import ConfirmModal from "../../../global/components/ConfirmModal";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PageNotFound } from "../../../global/components/PageNotFound";
import MyPaymentForm from "../../../global/components/PaymentForm";
import useDelete from "../../../global/hooks/useDelete";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import {
  getDealerNo,
  getOemNo,
  hasRole,
} from "../../../global/hooks/getUserRole";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import PaymentIcon from "../../../assets/images/vectors/SideBarIcons/PaymentIcon";
import {
  getOptionLabel,
  renderPrice,
  termOptions,
} from "../../../utils/Common";
import { PurchaseLicenseCodeSelector } from "../../MySite/Components/PurchaseLicenseCodeSelector";
import {
  getPurchaseListPath,
  getPurchaseOpenPath,
  getPurchaseRowId,
} from "../utils/listSearchStorage";

const PRE_PURCHASE_TYPE = 1;
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

const hasValue = (value) => value != null && value !== "";

const formatMoney = (value) =>
  hasValue(value) && Number.isFinite(Number(value))
    ? `$${Number(value).toFixed(2)}`
    : "";

const roundMoney = (value) => Math.round(value * 100) / 100;

const getPendingKey = (codeNo) => `pending-${codeNo}`;

const getItemsColumns = ({ onCountChange }) => [
  {
    title: "Item",
    dataIndex: "ItemName",
    key: "ItemName",
    onHeaderCell: () => ({ onClick: () => {} }),
  },
  {
    title: "Code",
    dataIndex: "CodeName",
    key: "CodeName",
    onHeaderCell: () => ({ onClick: () => {} }),
  },
  {
    title: "Term",
    dataIndex: "Term",
    key: "Term",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: (value) => getOptionLabel(termOptions, Number(value)) || "",
  },
  {
    title: "Unit Price",
    dataIndex: "Price",
    key: "Price",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: renderPrice,
  },
  {
    title: "Count",
    dataIndex: "ItemCount",
    key: "ItemCount",
    width: 130,
    align: "center",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: (value, record) =>
      record.isPending && onCountChange ? (
        <InputNumber
          size="small"
          min={1}
          max={9999}
          precision={0}
          value={value}
          onChange={(next) => onCountChange(record.key, next)}
          style={{ width: 90 }}
        />
      ) : (
        (value ?? 1)
      ),
  },
  {
    title: "Price",
    key: "Amount",
    onHeaderCell: () => ({ onClick: () => {} }),
    render: (_, record) =>
      renderPrice(Number(record.Price || 0) * Number(record.ItemCount || 1)),
  },
];

export const PrePurchaseForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isAdd = !id;
  const canManagePurchase = hasRole(1, 2, 7, 8);
  const isSystemUser = hasRole(1, 2, 3);
  const isDealerAccount = hasRole(7, 8);
  const showOem = isSystemUser;
  const showDealer = !isDealerAccount;

  const [pendingItems, setPendingItems] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [showItemsDeleteModal, setShowItemsDeleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [showCardLayout, setShowCardLayout] = useState(!isSystemUser);
  const [savingItems, setSavingItems] = useState(false);
  const [paidPayment, setPaidPayment] = useState(null);
  const [createdPurchase, setCreatedPurchase] = useState(null);

  const purchasesUrl = `${process.env.REACT_APP_API_URL}/api/purchases/`;
  const purchaseUrl = id ? `${purchasesUrl}${id}/` : null;
  const itemsUrl = id ? `${purchasesUrl}${id}/items/` : null;
  const {
    data: purchaseData,
    loading: purchaseLoading,
    error: purchaseError,
    refetch: purchaseRefetch,
  } = useGet(purchaseUrl);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);

  const { postData: postPurchase } = usePost(purchasesUrl);
  const { postData: postItem } = usePost(itemsUrl);
  const { error: putError, putData: putPurchase } = usePut(purchaseUrl);
  const {
    data: deletedPurchase,
    error: deletedPurchaseError,
    deleteData: deletePurchase,
  } = useDelete(purchaseUrl);
  const {
    data: deletedItems,
    error: deletedItemsError,
    deleteMultiple: deleteItems,
  } = useDelete(id ? `${purchasesUrl}${id}/items/bulk-delete/` : null);

  const purchase = purchaseData?.data;
  const watchedOemNo = Form.useWatch("OemNo", form);
  const watchedDealerNo = Form.useWatch("DealerNo", form);

  const { data: optionData, loading: optionLoading } = useGet(
    `${process.env.REACT_APP_API_URL}/api/accounts/options/`,
  );
  const modifiedOems = useMemo(
    () => getModifiedResponse(optionData, "oems"),
    [optionData],
  );
  const resolvedOemNo = useMemo(() => {
    const value = isAdd
      ? isSystemUser
        ? watchedOemNo
        : getOemNo()
      : purchase?.OemNo;
    if (!hasValue(value)) return null;
    if (Number.isFinite(Number(value))) return Number(value);
    return (
      optionData?.data?.oems?.find((oem) => String(oem?.name) === String(value))
        ?.value ?? null
    );
  }, [isAdd, isSystemUser, watchedOemNo, purchase?.OemNo, optionData]);
  const { data: dealerOptions, loading: dealerOptionsLoading } = useGet(
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null,
  );
  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptions, null),
    [dealerOptions],
  );
  const { data: oemData, loading: oemLoading } = useGet(
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/oems/${resolvedOemNo}/`
      : null,
  );
  const oemInfo = oemData?.data ?? oemData;
  const isOemLicense = Number(oemInfo?.OemLicense) === 1;

  const selectedDealerNo = isAdd
    ? isDealerAccount
      ? getDealerNo()
      : watchedDealerNo
    : purchase?.DealerNo;
  const dealerDiscount = useMemo(() => {
    if (!hasValue(selectedDealerNo)) return 0;
    const dealers = Array.isArray(dealerOptions?.data)
      ? dealerOptions.data
      : [];
    const dealer = dealers.find(
      (d) => Number(d?.value) === Number(selectedDealerNo),
    );
    const percent = Number(dealer?.discount);
    return Number.isFinite(percent) && percent > 0 ? Math.min(percent, 100) : 0;
  }, [dealerOptions, selectedDealerNo]);

  const savedItems = useMemo(
    () =>
      (itemsData?.data ?? [])
        .map((item) => ({ ...item, key: item.ItemNo, isPending: false }))
        .sort((a, b) => b.ItemNo - a.ItemNo),
    [itemsData],
  );
  const tableRows = [...pendingItems, ...savedItems];
  const pendingAmount = pendingItems.reduce(
    (sum, row) => sum + Number(row.Price || 0) * Number(row.ItemCount || 1),
    0,
  );
  const savedSubTotal = Number(purchase?.SubTotal ?? purchase?.Amount) || 0;
  const savedTotal = Number(purchase?.Total ?? purchase?.Amount) || 0;
  const subTotal = roundMoney(savedSubTotal + pendingAmount);
  const paymentAmount = roundMoney(
    savedTotal + pendingAmount * (1 - dealerDiscount / 100),
  );

  useEffect(() => {
    form.setFieldsValue({
      SubTotal: formatMoney(subTotal),
      Discount: `${dealerDiscount}%`,
      Total: formatMoney(paymentAmount),
    });
  }, [form, subTotal, dealerDiscount, paymentAmount]);

  useEffect(() => {
    if (!purchase) return;
    form.setFieldsValue({
      OemName: hasValue(purchase.OemNo) ? String(purchase.OemNo) : "",
      DealerNo: hasValue(purchase.DealerNo) ? Number(purchase.DealerNo) : null,
    });
  }, [form, purchase]);

  useEffect(() => {
    if (!isAdd || !isSystemUser || hasValue(form.getFieldValue("OemNo"))) {
      return;
    }
    const firstOem = modifiedOems[0]?.value;
    if (firstOem != null) form.setFieldsValue({ OemNo: firstOem });
  }, [isAdd, isSystemUser, modifiedOems, form]);

  useEffect(() => {
    if (!purchase || paymentModal || savingItems || paidPayment) return;
    const openPath = getPurchaseOpenPath(purchase, canManagePurchase);
    if (openPath !== `/purchase/prepurchase/${id}`) {
      navigate(openPath, { replace: true });
    }
  }, [
    purchase,
    canManagePurchase,
    id,
    navigate,
    paymentModal,
    savingItems,
    paidPayment,
  ]);

  useEffect(() => {
    if (!paymentModal) setShowCardLayout(!isSystemUser);
  }, [paymentModal, isSystemUser]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [putError]);

  useEffect(() => {
    if (deletedPurchaseError) {
      openErrorNotification(
        deletedPurchaseError?.response?.data?.message || "Something went wrong",
      );
    } else if (deletedPurchase) {
      openConfirmNotification("Success");
      navigate(getPurchaseListPath());
    }
  }, [deletedPurchase, deletedPurchaseError, navigate]);

  useEffect(() => {
    if (deletedItemsError) {
      openErrorNotification(
        deletedItemsError?.response?.data?.message || "Something went wrong",
      );
    } else if (deletedItems) {
      openConfirmNotification("Success");
      itemsRefetch();
      purchaseRefetch();
    }
  }, [deletedItems, deletedItemsError, itemsRefetch, purchaseRefetch]);

  if (
    !isAdd &&
    (purchaseError || (!purchaseLoading && purchaseData && !purchase))
  ) {
    return <PageNotFound />;
  }

  const onPickItem = (code) => {
    setPendingItems((prev) => [
      {
        key: getPendingKey(code.CodeNo),
        isPending: true,
        CodeNo: code.CodeNo,
        Item: code.Item,
        ItemName: code.ItemName,
        CodeName: code.CodeName,
        Term: code.Term,
        Price: code.Price,
        ItemCount: 1,
      },
      ...prev,
    ]);
  };

  const onCountChange = (key, count) =>
    setPendingItems((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, ItemCount: count || 1 } : row,
      ),
    );

  const onDeleteItemsConfirm = async () => {
    const savedKeys = selectedRowKeys.filter(
      (key) => !String(key).startsWith("pending-"),
    );
    setPendingItems((prev) =>
      prev.filter((row) => !selectedRowKeys.includes(row.key)),
    );
    setSelectedRowKeys([]);
    if (savedKeys.length) await deleteItems({ ids: savedKeys });
  };
  const savePendingItems = async (targetItemsUrl) => {
    const failed = [];
    for (const row of pendingItems) {
      const result = await postItem(
        {
          CodeNo: Number(row.CodeNo),
          ItemCount: Number(row.ItemCount) || 1,
        },
        targetItemsUrl,
      );
      if (result == null) failed.push(row);
    }
    setPendingItems(failed);
    return failed.length === 0;
  };

  const getPaymentNo = (paymentResponse) => {
    const payment =
      paymentResponse?.data?.data ?? paymentResponse?.data ?? paymentResponse;
    return payment?.PaymentNo ?? payment?.payment_no ?? payment?.id ?? null;
  };

  const getPurchasePaidPayload = (target, paymentResponse, amount) => ({
    PurchaseTime: target?.PurchaseTime
      ? dayjs(target.PurchaseTime).format(DATE_TIME_FORMAT)
      : null,
    PurchaseType: target?.PurchaseType ?? PRE_PURCHASE_TYPE,
    OemNo: resolvedOemNo ?? target?.OemNo,
    DealerNo: isAdd ? selectedDealerNo : target?.DealerNo,
    Amount: amount,
    PaymentNo: getPaymentNo(paymentResponse),
  });

  const createPurchase = async () => {
    const PurchaseTime = dayjs().format(DATE_TIME_FORMAT);
    const created = await postPurchase({
      PurchaseTime,
      PurchaseType: PRE_PURCHASE_TYPE,
      OemNo: resolvedOemNo,
      DealerNo: selectedDealerNo,
    });
    const createdData = created?.data ?? created;
    if (getPurchaseRowId(createdData) == null) return null;
    const next = {
      PurchaseTime,
      PurchaseType: PRE_PURCHASE_TYPE,
      ...createdData,
    };
    setCreatedPurchase(next);
    return next;
  };

  // A failed save keeps the payment and any created purchase, so a retry never charges or creates twice.
  const finalizePaidPurchase = async (paymentResponse, amount) => {
    setSavingItems(true);
    const fail = (message) => {
      setSavingItems(false);
      setPaidPayment({ response: paymentResponse, amount });
      if (!isAdd) itemsRefetch();
      openErrorNotification(message);
    };

    const target = isAdd
      ? (createdPurchase ?? (await createPurchase()))
      : purchase;
    if (!target) {
      fail(
        "Payment received, but the purchase could not be created. Click Save Items to retry.",
      );
      return;
    }
    const targetId = isAdd ? getPurchaseRowId(target) : id;

    const saved = pendingItems.length
      ? await savePendingItems(`${purchasesUrl}${targetId}/items/`)
      : true;
    if (!saved) {
      fail(
        "Payment received, but some items could not be saved. Click Save Items to retry.",
      );
      return;
    }

    const putResult = await putPurchase(
      getPurchasePaidPayload(target, paymentResponse, amount),
      `${purchasesUrl}${targetId}/`,
    );
    if (putResult == null) {
      fail(
        "Payment received, but the purchase could not be updated. Click Save Items to retry.",
      );
      return;
    }

    if (!isAdd) {
      // Info reads the same SWR cache entry; without this it sees the stale unpaid
      // purchase and bounces straight back here.
      await purchaseRefetch(
        (current) => ({
          ...current,
          data: {
            ...current?.data,
            ...putResult?.data,
            PaymentNo:
              putResult?.data?.PaymentNo ??
              getPaymentNo(paymentResponse) ??
              current?.data?.PaymentNo,
            PaymentTime:
              putResult?.data?.PaymentTime ??
              current?.data?.PaymentTime ??
              dayjs().format(DATE_TIME_FORMAT),
          },
        }),
        { revalidate: true },
      );
    }
    setSavingItems(false);
    setPaidPayment(null);
    navigate(`/purchase/info/${targetId}`, { replace: true });
  };

  const onPayment = async () => {
    if (paidPayment) {
      finalizePaidPurchase(paidPayment.response, paidPayment.amount);
      return;
    }
    if (isAdd) {
      const fields = [
        ...(showOem ? ["OemNo"] : []),
        ...(showDealer ? ["DealerNo"] : []),
      ];
      try {
        if (fields.length) await form.validateFields(fields);
      } catch {
        return;
      }
      if (!hasValue(selectedDealerNo)) {
        openErrorNotification("Dealer is required.");
        return;
      }
    }
    if (!(paymentAmount > 0)) {
      openErrorNotification("Total must be greater than 0 to make a payment.");
      return;
    }
    setPaymentModal(true);
  };

  const leavePage = () => navigate(getPurchaseListPath());
  const onLeave = () =>
    pendingItems.length || paidPayment ? setShowLeaveModal(true) : leavePage();

  const breadcrumbItems = [
    { label: "Purchase", href: "/purchase" },
    {
      label: "Pre-Purchase",
      href: isAdd ? "/purchase/prepurchase" : `/purchase/prepurchase/${id}`,
    },
  ];

  const buttonsData = [
    {
      label: paidPayment ? "Save Items" : "Payment",
      icon: paidPayment ? <CheckCircleOutlined /> : <FaDollarSign />,
      onClick: onPayment,
      loading: savingItems,
      disabled: savingItems || !tableRows.length || purchaseLoading,
    },
    ...(isAdd || paidPayment
      ? []
      : [
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => setShowDeleteModal(true),
            disabled: savingItems,
          },
        ]),
    isAdd
      ? {
          label: "Cancel",
          icon: <CloseOutlined />,
          onClick: onLeave,
          disabled: savingItems,
        }
      : {
          label: "List",
          icon: <UnorderedListOutlined />,
          onClick: onLeave,
        },
  ];

  const itemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: () => setItemModalOpen(true),
      disabled: savingItems || oemLoading,
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowItemsDeleteModal(true),
      disabled: savingItems || !selectedRowKeys.length,
    },
  ];

  const headerLocked = Boolean(paidPayment) || savingItems;

  return (
    <>
      <Form form={form} layout="vertical" autoComplete="off">
        <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
          <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
            <CommonButton buttonsData={buttonsData} />
          </CommonBreadcrumb>
        </div>

        <div className="bg-white overflow-hidden border rounded-[4px]">
          <div className="flex items-center bg-gray-200 p-2.5">
            <FaFileInvoiceDollar className="text-[18px] text-black" />
            <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
              Pre-Purchase
            </span>
          </div>
          <div className="px-2 pt-1 pb-3">
            <Row gutter={[16, 8]}>
              {showOem && (
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  {isAdd ? (
                    <CustomSelect
                      label="OEM"
                      name="OemNo"
                      options={modifiedOems}
                      placeholder="Please select"
                      required
                      allowClear={false}
                      disabled={headerLocked}
                      onChange={() => {
                        form.setFieldsValue({ DealerNo: null });
                        setPendingItems([]);
                        setSelectedRowKeys([]);
                      }}
                      loading={optionLoading}
                    />
                  ) : (
                    <CustomInput
                      label="OEM"
                      name="OemName"
                      disabled
                      loading={purchaseLoading}
                    />
                  )}
                </Col>
              )}
              {showDealer && (
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Dealer"
                    name="DealerNo"
                    options={modifiedDealers}
                    placeholder="Please select"
                    required={isAdd}
                    disabled={!isAdd || headerLocked || resolvedOemNo == null}
                    loading={purchaseLoading || dealerOptionsLoading}
                  />
                </Col>
              )}
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="SubTotal"
                  name="SubTotal"
                  disabled
                  loading={purchaseLoading}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="Discount"
                  name="Discount"
                  disabled
                  loading={purchaseLoading}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="Total"
                  name="Total"
                  disabled
                  loading={purchaseLoading}
                />
              </Col>
            </Row>
          </div>
        </div>
      </Form>

      <div className="flex justify-end mb-2">
        {paidPayment ? (
          <div className="mt-2" />
        ) : (
          <CommonButton buttonsData={itemButtonsData} />
        )}
      </div>
      <GlobalTable
        columns={getItemsColumns({
          onCountChange: paidPayment ? null : onCountChange,
        })}
        dataSource={tableRows}
        loading={itemsLoading}
        rowSelection={
          paidPayment
            ? undefined
            : { selectedRowKeys, onChange: setSelectedRowKeys }
        }
      />

      <Modal
        open={itemModalOpen}
        closable={false}
        onCancel={() => setItemModalOpen(false)}
        footer={null}
        width={1000}
        centered
        destroyOnClose
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaFileInvoiceDollar className="text-[18px] text-black" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add Pre-Purchase Item
            </span>
          </div>
        }
      >
        <PurchaseLicenseCodeSelector
          purchaseId={id}
          allowDuplicateItems
          disabledCodeNos={pendingItems.map((row) => row.CodeNo)}
          onPick={onPickItem}
          onCancel={() => setItemModalOpen(false)}
          oemNo={resolvedOemNo}
          isOemLicense={isOemLicense}
        />
      </Modal>

      <ConfirmModal
        showModal={showItemsDeleteModal}
        setShowModal={setShowItemsDeleteModal}
        onDeleteConfirm={onDeleteItemsConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <ConfirmModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        onDeleteConfirm={deletePurchase}
      />
      <ConfirmModal
        showModal={showLeaveModal}
        setShowModal={setShowLeaveModal}
        onDeleteConfirm={leavePage}
        content="Payment has not been completed. Any changes will be discarded. Do you want to leave?"
      />

      <Modal
        title={
          <div className="flex items-center bg-gray-200 p-2.5 rounded-md">
            <PaymentIcon className="text-[18px] text-black" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Make Payment
            </span>
          </div>
        }
        open={paymentModal}
        onCancel={() => setPaymentModal(false)}
        footer={null}
        closeIcon={false}
        width={560}
        centered
        destroyOnClose
      >
        <MyPaymentForm
          DealerNo={selectedDealerNo}
          amount={formatMoney(paymentAmount) || "$0.00"}
          setPaymentModal={setPaymentModal}
          InvoiceNo={id}
          OrderName={
            purchase?.PurchaseTime ? String(purchase.PurchaseTime) : "Purchase"
          }
          refetch={isAdd ? undefined : purchaseRefetch}
          showCardLayout={showCardLayout}
          setShowCardLayout={setShowCardLayout}
          onPaymentSuccess={async (paymentResponse) => {
            setPaymentModal(false);
            await finalizePaidPurchase(paymentResponse, paymentAmount);
          }}
        />
      </Modal>
    </>
  );
};
